import React from 'react';
import { Column } from 'react-table';
import { formatCurrency } from 'utils';
import { SELECT_ALL_VALUE } from 'components/Table/constants';
import { ProposalSaleType } from '@trnsact/trnsact-shared-types';
import { DateFilter } from 'components/Table/Filters/DateFilter';
import { InputFilter } from 'components/Table/Filters/InputFilter';
import { searchFilterByColumn } from 'components/Table/lib/searchFilters';
import { MultiSelectFilter } from 'components/Table/Filters/ColumnMultiSelectFilter';
import { HubProposal } from '../../../api/types';
import { getPersonaName } from '../../../lib/getPersonaName';
import { CellArgs, ProposalTableActions } from '../../../types';
import { proposalSaleTypeToFilterLAbel, proposalSaleTypeToViewText } from '../../../constants';
import { SimpleTextCell, StageCell, StatusCell, DateCell, CustomerCell, ActionCell } from '../cells';

interface Options extends ProposalTableActions<HubProposal> {
  tableData: HubProposal[];
}

type HubProposalCellArgs = CellArgs<HubProposal>;

export function createTableColumns({ tableData, ...actions }: Options) {
  const columns: Column<HubProposal>[] = [
    {
      width: 150,
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Created',
      accessor: 'createdDateTime',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: HubProposalCellArgs) => <DateCell proposal={original} />,
      Filter: filterProps => <DateFilter<HubProposal> {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Customer',
      accessor: 'businessName',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: HubProposalCellArgs) => <CustomerCell proposal={original} />,
      Filter: (filterProps: any) => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Contact',
      accessor: 'firstName',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={getPersonaName(original)} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Sale Type',
      accessor: 'saleType',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={original.saleType ? proposalSaleTypeToViewText[original.saleType] : null} />
      ),
      Filter: filterProps => (
        <MultiSelectFilter
          {...filterProps}
          rows={tableData}
          labelFormatter={type =>
            type === SELECT_ALL_VALUE ? type : proposalSaleTypeToFilterLAbel[type as ProposalSaleType]
          }
        />
      ),
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Sales Rep',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      accessor: 'vendorOpportunity.salesRepresentative.fullName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={getPersonaName(original?.vendorOpportunity?.salesRepresentative)} />
      ),
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Finance Manager',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '10rem' },
      filterMethod: searchFilterByColumn,
      style: { minWidth: '10rem' },
      accessor: 'vendorOpportunity.financeManager.fullName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={getPersonaName(original?.vendorOpportunity?.financeManager)} />
      ),
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Location',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      accessor: 'vendorOpportunity.location.locationName',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={original?.vendorOpportunity?.location?.locationName} />
      ),
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Equipment Condition',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      accessor: 'vendorOpportunity.equipments[0].newUsed',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell value={original?.vendorOpportunity?.equipments[0]?.newUsed.toLowerCase()} />
      ),
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Payment Method',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      accessor: 'vendorOpportunity.paymentType',
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={original?.vendorOpportunity?.paymentType} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Amount',
      accessor: 'amountRequested',
      headerClassName: 'customHeader',
      Cell: ({ original }: HubProposalCellArgs) => (
        <SimpleTextCell formatter={formatCurrency} value={original.amountRequested} />
      ),
      Filter: filterProps => <InputFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Opportunity Stage',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      filterMethod: searchFilterByColumn,
      style: { minWidth: '11rem' },
      accessor: 'vendorOpportunity.applicationStage',
      Cell: ({ original }: HubProposalCellArgs) => <StageCell proposal={original} />,
      Filter: filterProps => (
        <MultiSelectFilter
          {...filterProps}
          rows={tableData}
          labelFormatter={label => label.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
        />
      ),
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      accessor: 'status',
      Header: 'Proposal Status',
      headerClassName: 'customHeader',
      headerStyle: { minWidth: '11rem' },
      filterMethod: searchFilterByColumn,
      style: { minWidth: '11rem' },
      Cell: ({ original }: HubProposalCellArgs) => <StatusCell proposal={original} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: true,
      resizable: false,
      filterable: true,
      Header: 'Created By',
      accessor: 'salesPersonName',
      headerClassName: 'customHeader',
      filterMethod: searchFilterByColumn,
      Cell: ({ original }: HubProposalCellArgs) => <SimpleTextCell value={original.salesPersonName} />,
      Filter: filterProps => <MultiSelectFilter {...filterProps} rows={tableData} />,
    },
    {
      sortable: false,
      resizable: false,
      filterable: false,
      Header: 'Actions',
      style: { textAlign: 'center' },
      headerClassName: 'customHeader',
      headerStyle: { textAlign: 'center' },
      Cell: ({ original }: HubProposalCellArgs) => <ActionCell proposal={original} {...actions} />,
    },
  ];

  return columns;
}
